/**
 * VFX8 / Wave 2b: sun-direction-aware atmospheric scattering shell.
 *
 * Wave 2b upgrades the near look from a bare Fresnel-limb glow to a high-quality
 * *analytic single-scattering* approximation (Bruneton/Hillaire-flavoured, but a
 * closed-form WebGL2-budget model — no precomputed LUT, since the shell is a single
 * transparent sphere and a full transmittance/scattering LUT would be overkill for
 * one body). It combines:
 *   - a Rayleigh phase term (blue day haze, brightening toward the sun),
 *   - a Henyey–Greenstein forward-Mie halo (the bright sun-side limb / forward glow),
 *   - a terminator reddening band (sunset scattering where the sun grazes the air),
 *   - a limb-thickness (optical-depth) term so the air column reads thicker at the
 *     silhouette edge, and a hard night-limb cut so the dark edge never glows.
 * The result reads photographic at the cold-open + High Guard distances.
 *
 * The original Fresnel shell is preserved verbatim as ATMO_FRAG_SHELL_FARLOD for the
 * far-LOD (cheaper, and indistinguishable once the planet is a few pixels wide).
 * Extracted to its own module so the large GLSL template literals don't inflate
 * function-length checks in planetMaterials.ts.
 */
export const ATMO_VERT = /* glsl */ `
  varying vec3 vNormalW;
  varying vec3 vViewW;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vViewW = normalize(cameraPosition - wp.xyz);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

/**
 * Near-LOD analytic scattering. Uniforms are unchanged from the legacy shell
 * (sunDir, dayColor, sunsetColor, intensity) so callers/LOD swaps stay drop-in.
 */
export const ATMO_FRAG = /* glsl */ `
  varying vec3 vNormalW;
  varying vec3 vViewW;
  uniform vec3 sunDir;       // world-space direction planet -> Sol
  uniform vec3 dayColor;     // Rayleigh (blue) day haze
  uniform vec3 sunsetColor;  // Mie / terminator reddening
  uniform float intensity;

  // Rayleigh angular dependence: brighter looking along the sun (mu = cos angle).
  float rayleighPhase(float mu) { return 0.75 * (1.0 + mu * mu); }
  // Henyey-Greenstein forward scatter (the sun-side halo). g in (0,1) = forward.
  float hgPhase(float mu, float g) {
    float g2 = g * g;
    float d = 1.0 + g2 - 2.0 * g * mu;
    return (1.0 - g2) / (4.0 * 3.14159265 * max(pow(d, 1.5), 1e-4));
  }

  void main() {
    vec3 N = normalize(vNormalW);
    vec3 V = normalize(vViewW);
    vec3 S = normalize(sunDir);

    float NdotV = max(dot(N, V), 0.0);
    // Optical depth of the air column grows toward the limb (grazing view rays).
    float limb = pow(1.0 - NdotV, 2.2);
    float thickness = 0.35 + limb;

    float sun = dot(N, S);              // >0 day, ~0 terminator, <0 night
    float lit = smoothstep(-0.25, 0.35, sun);
    float mu = dot(V, -S);              // view vs sun: forward scatter when > 0

    // Rayleigh blue day haze, illumination- and phase-weighted along the air column.
    float rayleigh = rayleighPhase(mu) * lit * thickness;
    // Forward Mie halo: a bright rim looking toward the sun through the limb.
    float mie = hgPhase(mu, 0.76) * lit * limb * 6.0;
    // Terminator reddening: the narrow band where the sun grazes the atmosphere.
    float terminator = smoothstep(-0.15, 0.15, sun) * (1.0 - smoothstep(0.15, 0.6, sun));

    vec3 col = dayColor * rayleigh + sunsetColor * (mie * 0.5 + terminator * 0.9);
    float alpha = (rayleigh * 0.9 + mie * 0.6 + terminator * 0.5) * intensity;

    // Hard-fade the night-side limb so the dark edge does NOT glow.
    alpha *= smoothstep(-0.12, 0.25, sun);
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * FAR-LOD (legacy) Fresnel shell — the original Wave-8 shader, kept for the cheap
 * far tier. Bright on the sun-lit limb, blue day-side haze, orange terminator, hard
 * night-limb fade. Uniform-compatible with ATMO_FRAG for a hot material swap.
 */
export const ATMO_FRAG_SHELL_FARLOD = /* glsl */ `
  varying vec3 vNormalW;
  varying vec3 vViewW;
  uniform vec3 sunDir;
  uniform vec3 dayColor;
  uniform vec3 sunsetColor;
  uniform float intensity;
  void main() {
    vec3 N = normalize(vNormalW);
    vec3 V = normalize(vViewW);
    vec3 S = normalize(sunDir);
    float fres = pow(1.0 - max(dot(N, V), 0.0), 2.5);
    float sun = dot(N, S);
    float lit = smoothstep(-0.25, 0.35, sun);
    float terminator = smoothstep(0.0, 0.35, sun) * (1.0 - smoothstep(0.35, 0.8, sun));
    float forward = pow(max(dot(V, -S), 0.0), 2.0);
    vec3 col = mix(dayColor, sunsetColor, terminator);
    float alpha = fres * lit * (0.6 + 0.7 * forward) * intensity;
    alpha *= smoothstep(-0.15, 0.25, sun);
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Thin per-body atmosphere tint presets. Earth is the Rayleigh-blue showcase;
 * Mars is a thin dusty CO₂ butterscotch limb, Venus an opaque sulphuric cream haze,
 * Titan an orange organic-smog shell. `intensity` scales the shell's additive
 * contribution (Venus/Titan are optically thick → brighter limbs).
 */
export interface AtmospherePreset {
  /** Rayleigh (day-haze) colour, linear RGB in [0,1]. */
  day: readonly [number, number, number];
  /** Mie / terminator (sunset) colour, linear RGB in [0,1]. */
  sunset: readonly [number, number, number];
  intensity: number;
}

export type AtmosphereBody = 'earth' | 'mars' | 'venus' | 'titan';

export const ATMO_PRESETS: Record<AtmosphereBody, AtmospherePreset> = {
  earth: { day: [0.3, 0.55, 1.0], sunset: [1.0, 0.5, 0.2], intensity: 1.2 },
  mars: { day: [0.62, 0.44, 0.32], sunset: [0.75, 0.42, 0.24], intensity: 0.6 },
  venus: { day: [0.92, 0.86, 0.66], sunset: [0.98, 0.82, 0.55], intensity: 1.5 },
  titan: { day: [0.78, 0.5, 0.22], sunset: [0.85, 0.45, 0.18], intensity: 1.1 },
};
